import { useCallback } from 'react';
import { useAlert } from 'react-alert';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { useCancellationToken } from '../../../../hooks/useTokenCancellation';
import { useActions } from '../../../../hooks/useActions';
import { CONTRACT_RENEWAL_STATUS_EDITING_PROCESSING } from '../../../../store/redux-slices/processes/constants';
import { useModalDetails } from '../../../../hooks/useModalDetails';
import {
  useApiResponseError,
  useFormErrors,
} from '../../../FormErrorProvider/FormErrorProvider.hooks';
import { useProcessing } from '../../../../hooks/useProcessing';
import { updateModalState } from '../../../../store/redux-slices/modals';
import { IEditContractRenewalStatusPayload } from '../../../../store/saga-slices/contracts/models';
import { editContractRenewalStatus } from '../../../../store/saga-slices/contracts';
import { EDIT_RENEWAL_CONTRACT_STATUS_MODAL_NAME } from '../editRenewalContractStatusModal.const';
import { organizationNameSelector } from '../../../../store/redux-slices/organizations/selectors';
import { validateRenewalStatusForm } from '../../../Forms/RenewalStatusForm/renewalStatusForm.validation';
import { RenewalStatusFormModel } from '../../../Forms/RenewalStatusForm/renewalStatusForm.models';
import { EditRenewalContractStatusModalDetails } from '../editRenewalContractStatusModal.models';

type UseEditContractRenewalStatusProps = {
  onRenewalStatusEdited?: () => void;
};

export const useEditContractRenewalStatus = ({
  onRenewalStatusEdited,
}: UseEditContractRenewalStatusProps) => {
  const cancellationToken = useCancellationToken();
  const alert = useAlert();
  const { t } = useTranslation();

  const organizationName = useSelector(organizationNameSelector);

  const UpdateModalState = useActions(updateModalState);
  const EditContractRenewalStatus = useActions(editContractRenewalStatus);

  const isProcessing = useProcessing(
    CONTRACT_RENEWAL_STATUS_EDITING_PROCESSING,
  );

  const {
    contractId,
    organizationId,
    renewalStatus,
    contractName,
    expirationDate,
    organizationName: organizationNameDetails,
  }: EditRenewalContractStatusModalDetails = useModalDetails(
    EDIT_RENEWAL_CONTRACT_STATUS_MODAL_NAME,
  );

  const { validateErrors } = useFormErrors();

  const { handleResponseError } = useApiResponseError();

  const closeModalHandler = useCallback(() => {
    UpdateModalState(EDIT_RENEWAL_CONTRACT_STATUS_MODAL_NAME);
  }, [UpdateModalState]);

  const handleSubmit = useCallback(
    (values: RenewalStatusFormModel) => {
      const payload: IEditContractRenewalStatusPayload = {
        organizationId,
        contractId,
        newRenewalStatus: values.newRenewalStatus,
        cancellationToken,
        success: () => {
          alert.success(t('alerts.renewal-status-updated-success'));
          closeModalHandler();
          onRenewalStatusEdited?.();
        },
        error: (err: any) => {
          handleResponseError(err, values);
          alert.error(t('alerts.renewal-status-updated-fail'));
        },
      };
      EditContractRenewalStatus(payload);
    },
    [
      EditContractRenewalStatus,
      alert,
      cancellationToken,
      closeModalHandler,
      contractId,
      handleResponseError,
      onRenewalStatusEdited,
      organizationId,
      t,
    ],
  );

  const validateData = useCallback(
    (data: RenewalStatusFormModel) =>
      validateRenewalStatusForm(data, validateErrors(data)),
    [validateErrors],
  );

  const initialValues: RenewalStatusFormModel = {
    newRenewalStatus: renewalStatus,
  };

  return {
    closeModalHandler,
    handleSubmit,
    initialValues,
    organizationName: organizationName || organizationNameDetails,
    contractName,
    expirationDate,
    isProcessing,
    validateData,
  };
};
